const placeholderRegex = /([$!=])\{([^}]+)\}/g;

function getPlaceholderMaps(availablePlaceholders = []) {
  const placeholderTypeMap = {};
  const placeholderDisplayMap = {};
  const placeholdersList = availablePlaceholders[0]?.placeholders || availablePlaceholders;

  placeholdersList.forEach(p => {
    if (p.key) {
      placeholderTypeMap[p.key] = p.type || 'text';
      if (p.label && p.label !== p.key) {
        placeholderDisplayMap[p.key] = p.label;
      }
    }
  });

  return { placeholderTypeMap, placeholderDisplayMap };
}

function createPlaceholderSpan(doc, prefix, key, placeholderTypeMap, placeholderDisplayMap) {
  const span = doc.createElement('span');
  const type = placeholderTypeMap[key] || 'text';
  const displayKey = placeholderDisplayMap[key] || null;
  const isValid = Object.keys(placeholderTypeMap).length === 0 || key in placeholderTypeMap;

  span.setAttribute('data-placeholder-key', key);
  span.setAttribute('data-placeholder-prefix', prefix);
  span.setAttribute('data-placeholder-type', type);
  span.setAttribute('data-placeholder-valid', String(isValid));
  if (displayKey) {
    span.setAttribute('data-placeholder-display-key', displayKey);
  }
  span.className = 'placeholder-chip';
  span.textContent = `${prefix}{${displayKey || key}}`;

  return span;
}

function convertTextNode(textNode, placeholderTypeMap, placeholderDisplayMap) {
  const text = textNode.nodeValue;
  if (!text || !/[$!=]\{[^}]+\}/.test(text)) return;

  placeholderRegex.lastIndex = 0;
  const fragment = textNode.ownerDocument.createDocumentFragment();
  let lastIndex = 0;

  text.replace(placeholderRegex, (match, prefix, key, index) => {
    if (index > lastIndex) {
      fragment.appendChild(textNode.ownerDocument.createTextNode(text.slice(lastIndex, index)));
    }
    fragment.appendChild(createPlaceholderSpan(textNode.ownerDocument, prefix, key, placeholderTypeMap, placeholderDisplayMap));
    lastIndex = index + match.length;
    return match;
  });

  if (lastIndex < text.length) {
    fragment.appendChild(textNode.ownerDocument.createTextNode(text.slice(lastIndex)));
  }

  textNode.parentNode.replaceChild(fragment, textNode);
}

function shouldSkipTextNode(textNode) {
  let node = textNode.parentNode;
  while (node) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName?.toLowerCase();
      if (tag === 'a' || tag === 'script' || tag === 'style' || tag === 'textarea') {
        return true;
      }
      if (node.hasAttribute?.('data-placeholder-key')) {
        return true;
      }
    }
    node = node.parentNode;
  }
  return false;
}

function extractPlaceholder(text = '') {
  const match = text.match(/[$!=]\{[^}]+\}/);
  return match?.[0] || '';
}

function repairCorruptedPlaceholderLink(anchor) {
  const href = anchor.getAttribute('href') || '';
  const text = anchor.textContent || '';
  const textPlaceholder = extractPlaceholder(text);

  if (!href.includes('data-placeholder-key') || !textPlaceholder) {
    return '';
  }

  anchor.textContent = text.replace(/^[$!=]\{[^}]+\}["']?\s*[^>]*>\s*/, '');
  return textPlaceholder;
}

function normalizePlaceholderLinks(root) {
  root.querySelectorAll('a').forEach(anchor => {
    const href = anchor.getAttribute('href') || '';
    const repairedHref = repairCorruptedPlaceholderLink(anchor);
    const hrefPlaceholder = extractPlaceholder(href);
    const isLegacyPlaceholderLink = Boolean(
      repairedHref ||
      hrefPlaceholder ||
      anchor.classList.contains('placeholder-chip') ||
      href.includes('data-placeholder-key')
    );

    if (!isLegacyPlaceholderLink) return;

    if (repairedHref) {
      anchor.setAttribute('href', repairedHref);
    }

    anchor.removeAttribute('class');
    anchor.removeAttribute('style');
    Array.from(anchor.attributes).forEach(attr => {
      if (attr.name.startsWith('data-placeholder-')) {
        anchor.removeAttribute(attr.name);
      }
    });
  });
}

function convertPlainTextPlaceholders(text, availablePlaceholders) {
  const { placeholderTypeMap, placeholderDisplayMap } = getPlaceholderMaps(availablePlaceholders);
  placeholderRegex.lastIndex = 0;

  return text.replace(placeholderRegex, (match, prefix, key) => {
    const type = placeholderTypeMap[key] || 'text';
    const displayKey = placeholderDisplayMap[key] || null;
    const isValid = Object.keys(placeholderTypeMap).length === 0 || key in placeholderTypeMap;
    const displayAttr = displayKey ? ` data-placeholder-display-key="${displayKey}"` : '';

    return `<span data-placeholder-key="${key}" data-placeholder-prefix="${prefix}" data-placeholder-type="${type}" data-placeholder-valid="${isValid}"${displayAttr} class="placeholder-chip">${prefix}{${displayKey || key}}</span>`;
  });
}

/**
 * Converts legacy plain-text placeholders to TipTap nodes without touching
 * placeholders inside HTML attributes. Placeholder-backed legacy Jodit buttons
 * are normalized to ordinary text links.
 */
export function convertPlaceholderTextToNodes(html, availablePlaceholders = []) {
  if (!html || typeof html !== 'string') return html;

  // Saved prompts are plain text with ${key} tokens — convert directly (template
  // fragment text nodes have no parentElement, so the DOM walker path skips them).
  if (!html.includes('<')) {
    const converted = convertPlainTextPlaceholders(html, availablePlaceholders);
    return converted ? `<p>${converted}</p>` : '';
  }

  if (typeof document === 'undefined') {
    return html;
  }

  const { placeholderTypeMap, placeholderDisplayMap } = getPlaceholderMaps(availablePlaceholders);
  const template = document.createElement('template');
  template.innerHTML = html;

  normalizePlaceholderLinks(template.content);

  const showText = document.defaultView?.NodeFilter?.SHOW_TEXT || 4;
  const walker = document.createTreeWalker(template.content, showText);
  const textNodes = [];
  let currentNode = walker.nextNode();

  while (currentNode) {
    if (!shouldSkipTextNode(currentNode)) {
      textNodes.push(currentNode);
    }
    currentNode = walker.nextNode();
  }

  textNodes.forEach(textNode => convertTextNode(textNode, placeholderTypeMap, placeholderDisplayMap));

  return template.innerHTML;
}
